import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactVideoEditor } from '@reactvideoeditor/react-video-editor/react-video-editor';
import StartRenderModal from '@/components/modals/StartRenderModal';
import {
  getCreativeComplianceStatus,
  updateCreative,
  type CreativeUpdateBody,
} from '@/services/creatives';
import { listCreativeRenders } from '@/services/renders';
import { loadVideoEditorAutosave } from '@/rve/loadVideoEditorAutosave';
import { streamingResolveAssets } from '@/services/assetStreaming';
import type { Creative, RenderJobSummary, RveCreative, Template } from '@/types/api';

function normalizeTracks(raw: unknown[] | undefined): unknown[] {
  if (!raw?.length) return [];
  return raw.map((t) => ({
    magnetic: false,
    muted: false,
    visible: true,
    ...(t as Record<string, unknown>),
  }));
}

function isAspectlyRelativeAssetUri(raw: string): boolean {
  return /^organization-\d+\/assets\/./.test(raw.trim());
}

function collectRelativeAssetUris(tracksInput: unknown[]): string[] {
  const out = new Set<string>();
  for (const raw of tracksInput) {
    const items = Array.isArray((raw as Record<string, unknown>).items)
      ? ((raw as Record<string, unknown>).items as unknown[])
      : [];
    for (const it of items) {
      const row = it as Record<string, unknown>;
      const cand =
        typeof row.src === 'string'
          ? row.src
          : typeof row.uri === 'string'
            ? row.uri
            : '';
      const s = cand.trim();
      if (s && isAspectlyRelativeAssetUri(s)) {
        out.add(s);
      }
    }
  }
  return [...out];
}

function applyStreamingToTracks(
  tracksInput: unknown[],
  urls: Record<string, { content_url: string }>,
): unknown[] {
  return tracksInput.map((track) => {
    const raw = track as Record<string, unknown>;
    const items = Array.isArray(raw.items)
      ? (raw.items as Record<string, unknown>[]).map((item) => {
          const cand =
            typeof item.src === 'string'
              ? item.src
              : typeof item.uri === 'string'
                ? item.uri
                : '';
          const trimmed = cand.trim();
          const entry =
            trimmed && urls[trimmed] ? urls[trimmed] : undefined;
          const nextUrl = entry?.content_url;
          if (!nextUrl) return { ...item };
          const nextItem = {
            ...item,
            src: nextUrl,
          };
          if (typeof item.content === 'string') {
            (nextItem as Record<string, unknown>).content = nextUrl;
          }
          return nextItem;
        })
      : raw.items;
    return {
      ...raw,
      items,
    };
  });
}

export interface RveCreativeEditorPageProps {
  creative: Creative;
  template: Template;
  creativeId: number;
  campaignId?: number;
  onCreativeUpdated: (c: Creative) => void;
}

export function RveCreativeEditorPage({
  creative,
  template,
  creativeId,
  campaignId,
  onCreativeUpdated,
}: RveCreativeEditorPageProps) {
  const navigate = useNavigate();
  const rc = creative.rve_creative!;
  const projectId = String(creativeId);
  const [autosaveEnabled, setAutosaveEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [complianceLabel, setComplianceLabel] = useState<string>('');
  const [complianceOverall, setComplianceOverall] = useState<string | null>(null);
  const [renderModalOpen, setRenderModalOpen] = useState(false);
  const [renders, setRenders] = useState<RenderJobSummary[]>([]);

  const backHref =
    campaignId != null ? `/campaigns/${campaignId}` : '/creatives';

  const defaultTracks = useMemo(() => normalizeTracks(rc.tracks as unknown[]), [rc.tracks]);
  const [playbackTracks, setPlaybackTracks] = useState<unknown[] | null>(null);

  const fps = rc.canvas.fps;
  const videoWidth = rc.canvas.width_px;
  const videoHeight = rc.canvas.height_px;

  const pollCompliance = useCallback(async () => {
    try {
      const row = await getCreativeComplianceStatus(creativeId);
      const st = row.compliance_status;
      const overall = st?.overall ?? null;
      setComplianceOverall(overall);
      if (!st) {
        setComplianceLabel('Compliance: not run');
        return;
      }
      setComplianceLabel(`Compliance: ${overall ?? '—'}`);
    } catch {
      setComplianceLabel('');
    }
  }, [creativeId]);

  useEffect(() => {
    void pollCompliance();
    const id = window.setInterval(() => void pollCompliance(), 4000);
    return () => window.clearInterval(id);
  }, [pollCompliance]);

  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      try {
        const normalized = normalizeTracks(rc.tracks as unknown[]);
        const keys = collectRelativeAssetUris(normalized);
        if (keys.length === 0) {
          if (!cancelled) setPlaybackTracks(normalized);
          return;
        }
        const map = await streamingResolveAssets(keys);
        if (!cancelled) setPlaybackTracks(applyStreamingToTracks(normalized, map));
      } catch {
        if (!cancelled) setPlaybackTracks(normalizeTracks(rc.tracks as unknown[]));
      }
    }
    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [creative.id, rc.tracks]);

  const buildFullRveBody = useCallback(async (): Promise<CreativeUpdateBody> => {
    const autosave = await loadVideoEditorAutosave(projectId);
    const tracksUnknown = (autosave?.tracks ?? rc.tracks) as unknown[];
    const durationMs =
      creative.render_duration_ms ??
      template.render_settings?.duration_ms ??
      rc.canvas.duration_ms ??
      60_000;
    const durationInFrames =
      autosave?.durationInFrames ??
      Math.max(1, Math.round((durationMs / 1000) * fps));
    const next: RveCreative = {
      schema_version: rc.schema_version,
      canvas: {
        fps,
        width_px: videoWidth,
        height_px: videoHeight,
        duration_ms: Math.round((durationInFrames / fps) * 1000),
      },
      tracks: tracksUnknown,
      custom: rc.custom ?? {},
    };
    return { rve_creative: next };
  }, [
    creative.render_duration_ms,
    fps,
    projectId,
    rc.canvas.duration_ms,
    rc.custom,
    rc.schema_version,
    rc.tracks,
    template.render_settings?.duration_ms,
    videoHeight,
    videoWidth,
  ]);

  const handleExplicitSave = useCallback(async () => {
    setSaveError('');
    setSaving(true);
    try {
      const body = await buildFullRveBody();
      const updated = await updateCreative(creativeId, body);
      onCreativeUpdated(updated);
      await pollCompliance();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [buildFullRveBody, creativeId, onCreativeUpdated, pollCompliance]);

  const handleAutosaved = useCallback(async () => {
    if (!autosaveEnabled) return;
    try {
      const st = await loadVideoEditorAutosave(projectId);
      if (!st?.tracks?.length) return;
      const updated = await updateCreative(creativeId, {
        rve_tracks: st.tracks,
      });
      onCreativeUpdated(updated);
    } catch {
      /* non-fatal */
    }
  }, [autosaveEnabled, creativeId, onCreativeUpdated, projectId]);

  useEffect(() => {
    void listCreativeRenders(creativeId, { limit: 5 }).then(setRenders).catch(() => {});
  }, [creativeId]);

  return (
    <div
      className="flex flex-col bg-dark text-on-surface"
      style={{ height: 'calc(100vh - 4rem)' }}
    >
      <header className="surface-dark flex shrink-0 items-center gap-3 border-b border-strong px-4 py-2">
        <button
          type="button"
          onClick={() => navigate(backHref)}
          className="text-on-surface-variant hover:text-on-surface flex items-center gap-1 rounded-lg px-2 py-1 text-sm"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-lg font-medium">{creative.name}</div>
          <div className="text-muted truncate text-xs">
            React Video Editor ·{' '}
            <Link to="/creatives" className="underline">
              All creatives
            </Link>
          </div>
        </div>
        {complianceLabel ? (
          <span className="text-muted rounded border border-muted px-2 py-1 text-xs">
            {complianceLabel}
          </span>
        ) : null}
        <label className="text-muted flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autosaveEnabled}
            onChange={(e) => setAutosaveEnabled(e.target.checked)}
          />
          Autosave tracks
        </label>
        <button
          type="button"
          className="bg-primary rounded px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          disabled={saving}
          onClick={() => void handleExplicitSave()}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="text-on-surface rounded border border-muted px-3 py-1.5 text-sm"
          onClick={() => setRenderModalOpen(true)}
        >
          Export
        </button>
      </header>
      {saveError ? (
        <div className="bg-red-950/40 px-4 py-2 text-sm text-red-200">{saveError}</div>
      ) : null}
      <div className="min-h-0 flex-1">
        <ReactVideoEditor
          projectId={projectId}
          defaultTracks={(playbackTracks ?? defaultTracks) as never}
          fps={fps}
          videoWidth={videoWidth}
          videoHeight={videoHeight}
          autoSaveInterval={autosaveEnabled ? 15_000 : undefined}
          showAutosaveStatus={autosaveEnabled}
          onSaved={() => void handleAutosaved()}
          enableWebRender={false}
          showRenderSettings
          defaultTheme="dark"
          showDefaultThemes={false}
          className="h-full"
        />
      </div>
      {renders.length > 0 ? (
        <footer className="text-muted shrink-0 border-t border-strong px-4 py-2 text-xs">
          Recent renders:{' '}
          {renders.map((r) => (
            <span key={r.id} className="mr-3">
              #{r.id} ({r.status})
            </span>
          ))}
        </footer>
      ) : null}
      <StartRenderModal
        isOpen={renderModalOpen}
        onClose={() => setRenderModalOpen(false)}
        creativeId={creativeId}
        creativeName={creative.name}
        complianceOverall={complianceOverall}
        onStarted={() =>
          void listCreativeRenders(creativeId, { limit: 5 }).then(setRenders)
        }
      />
    </div>
  );
}
